import React from "react";
import { Link } from "react-router-dom";
import { Container, Row, Col, Card, CardBody } from "reactstrap";

const TenantScreeningPolicy = () => {
  document.title = "Tenant Screening & FCRA Policy | Porcher";

  return (
    <div className="page-content">
      <Container>
        <Row className="justify-content-center">
          <Col lg={8}>
            <Card className="border-0 shadow-sm mt-4">
              <CardBody className="p-4">
                <h1 className="mb-3">Tenant Screening &amp; FCRA Policy</h1>
                <p className="text-muted small mb-4">Last updated: August 22, 2026</p>

                <div className="content">
                  <h5 className="mt-4">1. Purpose &amp; Scope</h5>
                  <p>
                    This policy explains how tenant screening works on the platform operated by Patronage Reservoir
                    LLC, a Nevada limited liability company doing business as Porcher ("Porcher", "we", "our", or
                    "us"), the roles Porcher and our screening vendor each play, and the rights an Applicant has under
                    the federal Fair Credit Reporting Act ("FCRA") and similar state laws. It applies whenever a
                    Homeowner requests, or an Applicant authorizes, a background screening through the Platform.
                  </p>

                  <h5 className="mt-4">2. What Screening May Include</h5>
                  <p>
                    Depending on the package a Homeowner selects, a screening request may include identity
                    verification, a credit report and credit score, criminal history, eviction history, and
                    income/employment verification. Not every package includes every check — the Applicant is shown
                    what a given request will cover before authorizing it.
                  </p>

                  <h5 className="mt-4">3. Porcher's Role vs. the Screening Vendor's Role</h5>
                  <p>
                    Screening reports are produced by an independent third-party consumer reporting agency ("CRA"),
                    not by Porcher. Porcher is not a consumer reporting agency: we do not compile, verify, score, or
                    guarantee the accuracy of any report, and we do not decide whether an application should be
                    approved or denied. The CRA is solely responsible for the accuracy of the report it produces, and
                    the Homeowner is solely responsible for how that report is used in their tenant-selection
                    decision.
                  </p>

                  <h5 className="mt-4">4. Applicant Consent &amp; Authorization</h5>
                  <p>
                    A screening report is only requested after the Applicant affirmatively authorizes it (a legal
                    requirement under the FCRA). An Applicant who does not wish to authorize a screening may withdraw
                    their rental application instead — no report is run without authorization.
                  </p>

                  <h5 className="mt-4">5. Permissible Purpose</h5>
                  <p>
                    Homeowners may only request a screening report for a bona fide rental application in connection
                    with a property they own or manage on the Platform, and may only use the report for that
                    tenant-selection decision. Reports may not be requested for any other purpose, reused for a
                    different applicant or property, or shared with anyone outside the tenant-selection process.
                  </p>

                  <h5 className="mt-4">6. How Reports Are Used</h5>
                  <p>
                    The Homeowner reviews the screening summary alongside the rest of the application and makes their
                    own tenant-selection decision. Porcher does not automatically approve, deny, or rank Applicants,
                    and screening results must be applied using the same criteria for every applicant to a given
                    listing — see our <Link to="/fair-housing-policy">Fair Housing &amp; Anti-Discrimination Policy</Link>.
                  </p>

                  <h5 className="mt-4">7. Adverse Action Process</h5>
                  <p>
                    If a Homeowner takes adverse action against an Applicant (for example, denying the application or
                    offering different terms) based in whole or in part on a screening report, the FCRA requires the
                    Homeowner, as the "user" of the report, to: (1) provide the Applicant a pre-adverse action notice
                    along with a copy of the report and a summary of the Applicant's rights under the FCRA; (2) allow
                    a reasonable waiting period for the Applicant to review and dispute the report; and (3) if the
                    decision stands, provide a final adverse action notice that includes the CRA's contact information
                    and a statement that the CRA did not make the decision and cannot explain the reason for it.
                    Porcher may provide tools that help deliver these notices, but the legal responsibility to issue
                    them belongs to the Homeowner.
                  </p>

                  <h5 className="mt-4">8. Accuracy &amp; Disputes</h5>
                  <p>
                    An Applicant who believes information in their screening report is inaccurate or incomplete has
                    the right to dispute it directly with the CRA that produced it, using the contact information
                    provided with the report. Porcher does not adjudicate disputes about report content, since we do
                    not generate the report ourselves.
                  </p>

                  <h5 className="mt-4">9. Data Handling for Screening</h5>
                  <p>
                    Where possible, sensitive identifiers (such as a Social Security number) are collected directly by
                    the screening vendor's own secure intake flow rather than stored in Porcher's database. Screening
                    results shown on the Platform are limited to the summary information necessary for the tenant
                    selection decision. See our <Link to="/privacy-policy">Privacy Policy</Link> for more on how
                    screening data is stored and who can access it.
                  </p>

                  <h5 className="mt-4">10. Non-Discrimination</h5>
                  <p>
                    Screening criteria must be applied consistently to every Applicant for a given listing and may
                    never be used as a pretext for discrimination based on a protected class. See our{" "}
                    <Link to="/fair-housing-policy">Fair Housing &amp; Anti-Discrimination Policy</Link>.
                  </p>

                  <h5 className="mt-4">11. Record Retention</h5>
                  <p>
                    Screening requests, authorizations, and results are retained for as long as necessary to support
                    the related application, comply with recordkeeping obligations, and resolve any dispute or adverse
                    action process, consistent with our <Link to="/privacy-policy">Privacy Policy</Link>.
                  </p>

                  <h5 className="mt-4">12. State &amp; Local Law Notices</h5>
                  <p>
                    Some states and cities impose additional screening requirements or restrictions — for example,
                    limits on the use of criminal history ("ban-the-box" style rules), source-of-income protections,
                    or specific adverse-action notice formats. Homeowners are responsible for knowing and complying
                    with the screening laws that apply to their property's location, in addition to this policy.
                  </p>

                  <h5 className="mt-4">13. Summary of Applicant Rights</h5>
                  <p>
                    Under the FCRA, Applicants generally have the right to: know when information in their file has
                    been used against them; know what is in their file; dispute inaccurate or incomplete information
                    with the CRA; receive a report free of charge under certain circumstances, including after
                    adverse action; and seek damages from violators in some cases. The official "Summary of Your
                    Rights Under the Fair Credit Reporting Act" is provided by the CRA or the Homeowner as part of the
                    adverse action process described above.
                  </p>

                  <h5 className="mt-4">14. Contact Us</h5>
                  <p>
                    If you have questions about this Tenant Screening &amp; FCRA Policy, please contact us through the
                    support portal.
                  </p>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default TenantScreeningPolicy;
